// An online game: the match, the wire, and the DOM that only exists online —
// the connection/rematch overlay, the ping button, and the opponent's draw
// and restart prompts.
import type { RoomClient } from '@bn-games/room-client';
import { announce } from '@bn-games/ui-shell';
import { playChime } from '@bn-games/audio-core';
import { t } from '../i18n/I18n.js';
import { GameRecorder } from '../history/GameRecorder.js';
import { commitRecord } from '../history/serverSync.js';
import { NetworkGameController } from '../network/NetworkGameController.js';
import type { GameOverResult, OverlayView } from '../network/overlayState.js';
import type { ChessSnapshot, ClientMessage, ServerMessage } from '../network/protocol.js';
import type { Outcome } from '../game/outcome.js';
import type { Color } from '../game/types.js';
import type { TurnBannerView } from '../render/overlay/TurnBanner.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import { createOnlineOverlayHost } from '../ui/OnlineOverlayHost.js';
import { createExportRow } from '../ui/exportRow.js';
import { OnlineMatch, type DrawEvent } from './OnlineMatch.js';
import { OnlinePing } from './OnlinePing.js';
import type { ModeHooks, SceneMode } from './SceneMode.js';
import { drawPrompt, restartPrompt } from './onlinePrompts.js';

/** How long "Draw offer declined" stays in the banner. */
const NOTICE_MS = 4_000;

export type ChessRoom = RoomClient<ClientMessage, ServerMessage>;

export interface OnlineModeDeps {
  readonly room: ChessRoom;
  readonly settings: SettingsManager;
  readonly hooks: ModeHooks;
  /** Leave the table for the menu. */
  readonly onLeave: () => void;
  /** The restart handshake moved: the pause menu re-reads `restartState`. */
  readonly onRestartStateChange: () => void;
}

function resultFor(outcome: Outcome, color: Color): GameOverResult {
  if (outcome.winner === null) return 'draw';
  return outcome.winner === color ? 'win' : 'loss';
}

export function createOnlineMode(deps: OnlineModeDeps): SceneMode {
  const drawAsk = drawPrompt();
  const restartAsk = restartPrompt();
  let notice: { readonly text: string; readonly until: number } | null = null;
  /** The match's snapshot inlet, known once the match has connected. */
  let feed: (snapshot: ChessSnapshot) => void = () => {};

  const opponentName = (): string => net.opponent?.name ?? t('common.opponent');
  const changed = (): void => {
    deps.onRestartStateChange();
    deps.hooks.onChange();
  };

  const net: NetworkGameController = new NetworkGameController({
    room: deps.room,
    onSnapshot: (snapshot) => {
      feed(snapshot);
      record(snapshot);
    },
    onChange: changed,
    onPinged: () => {
      if (deps.settings.get().afkRemindersEnabled) playChime();
    },
    onRestartProposal: (open) => {
      if (open) restartAsk.open(opponentName(), (accept) => net.send({ type: accept ? 'RESTART_ACCEPT' : 'RESTART_DECLINE' }));
      else restartAsk.close();
    },
  });

  const match = new OnlineMatch({
    connect: (onSnapshot) => {
      feed = onSnapshot;
      return net;
    },
    onMove: (played) => {
      if (played.by === 'remote') net.opponentSeen();
      deps.hooks.onMove(played);
    },
    onChange: changed,
    onNewGame: () => {
      exportRow = null;
      net.newGame();
      deps.hooks.onNewGame();
    },
    onDrawEvent: (event) => onDrawEvent(event),
  });

  function onDrawEvent(event: DrawEvent): void {
    if (event === 'received') {
      drawAsk.open(opponentName(), (accept) => match.answerDraw(accept));
      return;
    }
    drawAsk.close();
    if (event !== 'declined') return;
    const text = t('online.draw.declined');
    notice = { text, until: Date.now() + NOTICE_MS };
    announce(text);
  }

  /**
   * History and the resume label both follow the server's snapshots, never
   * the move this client showed ahead of the echo: a mate we played is not a
   * result until the server says so.
   */
  const recorder = new GameRecorder();
  /** PGN/FEN export for the game just recorded, shown under the result. */
  let exportRow: HTMLElement | null = null;
  let lastView: OverlayView | null = null;
  function record(snapshot: ChessSnapshot): void {
    const info = deps.room.getInfo();
    const color = net.localColor;
    if (info.roomCode === null || color === null) return;
    const localName = info.players?.find((p) => p.seat === info.localSeat)?.name ?? deps.settings.get().playerName;
    const finished = recorder.observe(snapshot, { roomCode: info.roomCode, color, localName, opponentName: opponentName() });
    if (finished === null) return;
    commitRecord(finished);
    exportRow = createExportRow(finished);
    // Our own mating move shows the result before the server confirms it: add the export to that dialog.
    if (lastView !== null) overlay.update(lastView);
  }

  const overlay = createOnlineOverlayHost({
    result: () => (match.state.outcome === null ? null : { outcome: match.state.outcome, perspective: match.humanColor }),
    opponent: () => net.opponent,
    exportRow: () => exportRow,
    requestRematch: () => net.requestRematch(),
    acceptRematch: () => net.send({ type: 'REMATCH_ACCEPT' }),
    declineRematch: () => net.declineRematch(),
    dismiss: () => net.dismissOverlay(),
    leave: deps.onLeave,
  });
  net.onOverlayChange((view) => {
    lastView = view;
    overlay.update(view);
    deps.hooks.onChange();
  });

  const ping = new OnlinePing({
    isEnabled: () => deps.settings.get().showAfkPingButton,
    send: () => net.send({ type: 'PING_OPPONENT' }),
  });

  function banner(): TurnBannerView | null {
    const dot = match.state.turn;
    if (!match.isSynced) return { text: t('online.status.waitingForOpponent'), tone: 'turn', dot: null };
    if (match.state.isOver) return null;
    if (notice !== null && Date.now() < notice.until) return { text: notice.text, tone: 'turn', dot };
    if (net.opponentAway && !match.canHumanMove()) {
      return { text: t('online.banner.opponentAway', { name: opponentName() }), tone: 'turn', dot };
    }
    // The offer was made from the pause menu: this is where the player sees it is on the table.
    if (match.drawOfferFrom === match.humanColor) return { text: t('online.draw.pending'), tone: 'turn', dot };
    return null;
  }

  return {
    match,
    online: true,
    perspective: () => match.humanColor,
    rotation: () => (match.humanColor === 'b' ? 180 : 0),
    tick: () => {
      const waiting = match.isSynced && !match.state.isOver && !match.canHumanMove() && net.playable && !net.opponentAway;
      ping.setWaiting(waiting);
    },
    banner,
    drawButton: () => ({
      label: t(match.drawOfferFrom === match.humanColor ? 'online.draw.pending' : 'game.draw.offer'),
      enabled: match.canOfferDraw(),
    }),
    offerDraw: () => match.offerDraw(),
    showGameOver: (outcome) => net.showGameOver(resultFor(outcome, match.humanColor)),
    requestRestart: () => net.requestRestart(),
    restartState: () => net.restartState(),
    dispose: () => {
      match.dispose();
      overlay.dispose();
      ping.dispose();
      drawAsk.close();
      restartAsk.close();
    },
  };
}
